import { buildHeader, buildSecureApiHeader, buildQueryParam } from "./baseService";
import { getJson } from "../utils/httpHelper";

export const getSubscriptionPlans = async (baseUrl, apiKey, channelId, params) => {
  const subscriptionPlans = await fetch(
    buildQueryParam(`${baseUrl}/subscription-plan/${channelId}/list/`, params),  
    {  
      method: "GET",
      headers: buildHeader(apiKey),
    }
  );
  return getJson(subscriptionPlans);
};

export const processSubscriptionCheckout = async (baseUrl, data) => {
  const response = await fetch(
    `${baseUrl}/billing/subscription/subscribe/`,
    {
      method: "POST",
      headers: buildSecureApiHeader(),
      body: data,
    }
  );
  const responseJson = getJson(response);
  if (response.status !== 200) {
    return {
      status: "F",
      message: responseJson.message,
    };
  }
  return { status: "S", message: responseJson };
};

export const verifyCouponCode = async (baseUrl, apiKey, data) => {
  const response = await fetch(
    `${baseUrl}/billing/offer/verify/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: data,
    }
  );

  return getJson(response);
}

export const purchasePPVOrder = async (baseUrl, apiKey, data) => {
  const response = await fetch(
    `${baseUrl}/billing/order/purchase/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: data,
    }
  );

  return getJson(response);
}

export const purchaseSubscription = async (baseUrl, apiKey, data) => {
  const response = await fetch(
    `${baseUrl}/billing/subscription/subscribe/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: data,
    }
  );

  return getJson(response);
}

export const generateDpoPayPortal = async (baseUrl, apiKey, data) => {
  try {
    const response = await fetch(`${baseUrl}/billing/subscription/generate-dpo-pay-portal/`, 
      {
        method: "POST",
        headers: buildHeader(apiKey),
        body: JSON.stringify({ data }),
      });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `DPO Pay Portal request failed with status ${response.status}: ${errorData.error || "Unknown error"}`
      );
    }

    const { payment_url } = await response.json();
    return payment_url;
  } catch (error) {
    console.error("Error generating DPO Pay Portal:", error);
    throw error;
  }
};

export const verifyTransaction = async (baseUrl, apiKey,companyToken, transactionToken) => {
  try {
    // API endpoint for transaction verification
    const endpoint = `${baseUrl}/billing/subscription/verify-dpo-transaction/`;

    // Make the POST request
    const response = await fetch(endpoint, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({ companyToken, transactionToken }),
    });

    // Check if the response is successful
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Verification failed with status ${response.status}: ${
          errorData.error || "Unknown error"
        }`
      );
    }

    // Parse the JSON response
    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error during transaction verification:", error);
    throw new Error("Verification failed. Please try again later.");
  }
};

export const getActiveGateway = async (baseUrl) => { 
  const response = await fetch(`${baseUrl}/gateway-setting/list/`, {
    headers: buildSecureApiHeader(),
  });

  const gateways = await getJson(response);
  return gateways.filter(gateway => gateway.is_default)[0];
}

export const getGatewayActive = async (baseUrl, apiKey) => {
  const response = await fetch(`${baseUrl}/gateway-setting/list-v2/`, {
    headers: buildHeader(apiKey),
  });

  const gateways = await getJson(response);
  return gateways.filter(gateway => gateway.is_default)[0];
}

export const appContentAccess = async (baseUrl, apiKey) => {
  const response = await fetch(`${baseUrl}/billing/subscription/app-content/`, {
    headers: buildHeader(apiKey),
  });

  return await getJson(response);
}

export const fetchPaypalAccessToken = async (baseUrl, apiKey, client_id, client_password) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/generate-access-token/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        client_id: client_id,
        client_secret: client_password,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return data.access_token; // Assuming the response contains the access token
    } else {
      throw new Error("Failed to fetch access token");
    }
  } catch (error) {
    console.error("Error fetching access token:", error);
    throw error;
  }
}

export const createPaypalOrder = async (baseUrl, apiKey,  orderPayload) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/create-order/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify(orderPayload),
    });

    const data = await response.json();

    if (response.ok && data.links) {
      const approvalLink = data.links.find((link) => link.rel === "approve");
      if (approvalLink) {
        return { orderId: data.id, approvalUrl: approvalLink.href };
      } else {
        throw new Error("Approval link not found.");
      }
    } else {
      throw new Error(data.message || "Failed to create PayPal order.");
    }
  } catch (error) {
    console.error("Error creating PayPal order:", error);
    throw error;
  }
};

export const capturePaypalOrder = async (baseUrl, apiKey, accessToken, orderId) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/capture-order/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        order_id: orderId,
      }),
    });

    const data = await response.json();
    console.log("Data is ", data);

    // Check if the response is OK and if the success flag is true
    if (response.ok && data.success) {
      return { success: true, response_data: data.data };
    } else {
      return { success: false, response_data: data.data };
    }
  } catch (error) {
    console.error("Error capturing PayPal order:", error);
    return false; // Return false if there is an error
  }
};

export const checkOrCreatePaypalProduct = async (baseUrl, apiKey, accessToken, productData) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/check-or-create-product/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        ...productData,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Returns the created or existing product details
    } else {
      throw new Error(data.message || "Failed to check or create product.");
    }
  } catch (error) {
    console.error("Error checking or creating PayPal product:", error);
    throw error;
  }
};

export const createPaypalSubscriptionPlan = async (baseUrl, apiKey, accessToken, planData) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/create-subscription-plan/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        ...planData,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Returns the created subscription plan details
    } else {
      throw new Error(data.message || "Failed to create subscription plan.");
    }
  } catch (error) {
    console.error("Error creating PayPal subscription plan:", error);
    throw error;
  }
};

export const createPaypalSubscription = async (baseUrl, apiKey, accessToken, subscriptionData) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/create-subscription/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        ...subscriptionData,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Returns subscription details including approval URL
    } else {
      throw new Error(data.message || "Failed to create subscription.");
    }
  } catch (error) {
    console.error("Error creating PayPal subscription:", error);
    throw error;
  }
};

export const getPaypalSubscriptionDetails = async (baseUrl, apiKey, accessToken, subscriptionId) => {
  try {
    console.log("getPaypalSubscriptionDetails, accessToken:", accessToken);

    const response = await fetch(`${baseUrl}/billing/paypal/subscription-details/`, {
      method: "POST", // Use POST instead of GET
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken, // Send accessToken in the body
        subscription_id: subscriptionId, // Subscription ID
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Return PayPal subscription details
    } else {
      throw new Error(data.message || "Failed to retrieve subscription details.");
    }
  } catch (error) {
    console.error("Error retrieving PayPal subscription details:", error);
    throw error;
  }
};
